import React, { useState } from 'react';

import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import {
  faCheckDouble,
  faCloudArrowDown,
  faCopy,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';

import { postMessageActionTaken } from '../../../slices/LeoSlice';
import { getAuthCookie } from '../../../utils/AuthUtils';
import { copyImageToClipboard, getFileBlob } from '../../../utils/FileUtils';
import {
  getDownloadableImageUrl,
  handleLeoListingPosterDownload,
  LeoMediaItem,
} from '../../../utils/LeoUtils';

import Button from '../../Button';
import { AppDispatch } from '../../../types';
import Markdown from '../Chat/MarkDownRender';

type NeoLeoGenericShareablesInfoSectionProps = {
  msgId: string;
  shareablesInfo: LeoMediaItem;
};

const NeoLeoGenericShareablesInfoSection: React.FC<
  NeoLeoGenericShareablesInfoSectionProps
> = ({
  msgId,
  shareablesInfo: { title, sub_title, media_url, mark_down, template_url },
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isCopyingImage, setIsCopyingImage] = useState<boolean>(false);
  const [isImageCopied, setIsImageCopied] = useState<boolean>(false);

  const handleDownload = async (posterLink: string) => {
    setIsDownloading(true);
    await handleLeoListingPosterDownload(posterLink);
    dispatch(
      postMessageActionTaken(msgId, {
        text: 'Download',
        payload: {
          action_type: 'IMAGE_DOWNLOADED',
        },
      }),
    );
    setIsDownloading(false);
  };

  const copyImage = async () => {
    setIsCopyingImage(true);
    // this is slow because it downloads the image from server, not a cdn
    const blob = await getFileBlob(getDownloadableImageUrl(media_url), {
      headers: new Headers({
        Authorization: `Bearer ${getAuthCookie()}`,
      }),
    });
    const blobUrl = URL.createObjectURL(blob!);
    await copyImageToClipboard(blobUrl);
    dispatch(
      postMessageActionTaken(msgId, {
        text: 'Copy Image',
        payload: {
          action_type: 'IMAGE_DOWNLOADED',
        },
      }),
    );
    setIsCopyingImage(false);
    setIsImageCopied(true);
    setTimeout(() => setIsImageCopied(false), 2000);
  };

  return (
    <div className='flex flex-col p-6 relative bg-white h-full w-full overflow-y-scroll font-inter'>
      <div className='pb-6 w-full'>
        <div className='flex-col justify-center pb-6'>
          <p className='font-semibold text-xl text-primary-dark font-inter'>
            {title}
          </p>
          {sub_title && (
            <span className='font-normal text-lg text-primary-dark pt-2 font-inter'>
              {sub_title}
            </span>
          )}
        </div>
        <div className='w-full flex flex-col lg:flex-row flex-wrap mt-2 justify-between'>
          <div className='lg:w-1/2 lg:pr-1 pb-2 md:mb-0 w-full'>
            <Button
              label='Download'
              leftIcon={
                !isDownloading ? (
                  <FontAwesomeIcon
                    icon={faCloudArrowDown}
                    className='text-white mr-1'
                  />
                ) : undefined
              }
              onPress={() => handleDownload(media_url)}
              isSubmitting={isDownloading}
              isDisabled={isDownloading}
              variant='primary-outline'
              textSize='md'
              containerStyle='bg-reskin-regent-400 !outline-none !border-none text-white !font-inter !font-normal !h-full !py-2.5'
              fullWidth
            />
          </div>
          <div className='lg:w-1/2 lg:pl-1 w-full'>
            {template_url ? (
              <Button
                variant='no-border'
                label='Customize'
                leftIcon={
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    className='text-primary-dark mr-1'
                  />
                }
                onPress={() => window.open(template_url, '_blank')}
                containerStyle='bg-reskin-regent-400 !font-inter'
                fullWidth
              />
            ) : (
              <Button
                variant='no-border'
                label={isCopyingImage ? 'Copying image' : 'Copy image'}
                leftIcon={
                  !isCopyingImage ? (
                    <FontAwesomeIcon
                      icon={isImageCopied ? faCheckDouble : faCopy}
                      className='text-primary-dark mr-1'
                    />
                  ) : undefined
                }
                onPress={copyImage}
                containerStyle='bg-reskin-regent-400 !font-inter'
                fullWidth
                isDisabled={isCopyingImage}
                isSubmitting={isCopyingImage}
              />
            )}
          </div>
        </div>
      </div>
      {/* {!mark_down && ( */}
      <div className='flex flex-colpt-6 leo-shareable'>
        <Markdown content={mark_down ?? '### Sample Markdown'} msgId={msgId} />
      </div>
      {/* )} */}
    </div>
  );
};
export default NeoLeoGenericShareablesInfoSection;
